import { useEffect, useMemo } from 'react'
import { shallowEqual } from 'react-redux'
import {
  ONBOARDING_CONTACT_SYNC_SUPPORTED_INTEGRATIONS,
  CONTACT_SYNC_CHECKPOINT_OBJECT,
  DEFAULT_CONTACT_SYNC_CHECKPOINT_OBJECT,
  IntegrationServiceId,
} from '../../../../common/integrations'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { useGetMeQuery } from '../../../../store/user/user'
import { syncApi, type CheckpointRequest } from '../../../../store/sync/sync'

const contactSyncCheckpointRequest = (accountId: string, id: IntegrationServiceId): CheckpointRequest => ({
  accountId,
  level: 'user',
  connector: id,
  direction: 'inbound',
  object: CONTACT_SYNC_CHECKPOINT_OBJECT[id] ?? DEFAULT_CONTACT_SYNC_CHECKPOINT_OBJECT,
})

export interface ContactSyncStates {
  contactSyncActive: Set<IntegrationServiceId>
  isLoading: boolean
}

export const useContactSyncStates = (): ContactSyncStates => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { data: me, isLoading: isMeLoading } = useGetMeQuery({ accountId })

  const connectedIds = useMemo(() => new Set(me?.integrations?.map((i) => i.serviceId) ?? []), [me])

  useEffect(() => {
    if (!accountId) {
      return
    }

    const subs = ONBOARDING_CONTACT_SYNC_SUPPORTED_INTEGRATIONS.filter((id) => connectedIds.has(id)).map((id) =>
      dispatch(syncApi.endpoints.getCheckpoint.initiate(contactSyncCheckpointRequest(accountId, id))),
    )

    return () => subs.forEach((s) => s.unsubscribe())
  }, [accountId, connectedIds, dispatch])

  const activeContactSyncIntegrationIds = useAppSelector(
    (state) =>
      !accountId
        ? []
        : ONBOARDING_CONTACT_SYNC_SUPPORTED_INTEGRATIONS.filter(
            (id) =>
              connectedIds.has(id) && syncApi.endpoints.getCheckpoint.select(contactSyncCheckpointRequest(accountId, id))(state)?.data?.data?.active === true,
          ),
    shallowEqual,
  )

  const isAnyCheckpointPending = useAppSelector((state) => {
    if (!accountId) {
      return false
    }
    return ONBOARDING_CONTACT_SYNC_SUPPORTED_INTEGRATIONS.some(
      (id) => connectedIds.has(id) && syncApi.endpoints.getCheckpoint.select(contactSyncCheckpointRequest(accountId, id))(state)?.status === 'pending',
    )
  })

  const contactSyncActive = useMemo(() => new Set(activeContactSyncIntegrationIds), [activeContactSyncIntegrationIds])
  const isLoading = isMeLoading || isAnyCheckpointPending

  return useMemo(() => ({ contactSyncActive, isLoading }), [contactSyncActive, isLoading])
}
